/* CUSTOM (Carver/Loaded/Orangatang shared): mega-menu behaviour + deferred
   image hydration.

   PART 1 - navigation behaviour (wave 26.09, LB navigation audit, batch A).
   Progressive enhancement: without this file the stock CSS hover menu and the
   touch drawer behave exactly as before. Skipped inside the theme editor.
   Desktop mega menu:
     - hover intent: a panel opens after a short dwell, closes after a short
       grace period, and switches straight to a neighbour once one is open;
     - one panel at a time (hover and keyboard can no longer open two);
     - each dropdown item gets a real <button aria-expanded> (the caret), so
       Tab walks the top-level items instead of every link in every panel;
       Escape closes the panel and returns focus to the button;
     - on touch screens >= 992px the first tap opens the panel, the second
       follows the link;
     - the page dim follows the open state (lights-off-active) instead of raw
       :hover, so crossing the bar no longer flashes it.
   Mobile drawer:
     - rows, Back and Close become keyboard-operable named buttons;
     - focus lands on the drawer heading on open and follows each slide;
     - off-screen levels are inert; the drawer resets to its top level on close;
     - Escape runs the modal's own close routine;
     - the header icon triggers get a role, a tab stop and a name.
   Labels come from the theme's own translations via data attributes on the
   script tag in snippets/main-nav.liquid.

   PART 1b - panel fit: a mega panel whose configured columns would run past
   the viewport gets @pro-wrap (see main-nav-pro.css A1). Measured with the
   stock layout on load, resize and font load; runs in the editor too.

   PART 2 - deferred image hydration (wave 26.03).
   Menu images render with data-nav-src/data-nav-srcset (no src), so they cost
   nothing on initial page load. They hydrate:
     1. instantly on first hover/touch/focus of their nav item (before the
        dropdown's opening transition finishes), and
     2. as a staggered low-priority background warm-up after window load, so
        images are already cached when the user browses the menu.
   Country-selector flags use data-flag-src and hydrate ONLY on interaction
   with their dropdown (118 third-party flag images should never preload).
   On Save-Data or 2G connections the warm-up is skipped entirely. */

(function () {
  'use strict';
  if (window.Shopify && window.Shopify.designMode) return;

  var script = document.currentScript;
  var data = (script && script.dataset) || {};
  var LABEL = {
    back: data.labelBack || 'Back',
    close: data.labelClose || 'Close',
    menu: data.labelMenu || 'Menu',
    search: data.labelSearch || 'Search',
    cart: data.labelCart || 'Cart'
  };

  var DESKTOP = window.matchMedia('(min-width: 992px)');
  var OPEN_DELAY = 140;   /* dwell before a panel opens from rest */
  var SWITCH_DELAY = 90;  /* neighbour switch while one is open (diagonal moves) */
  var CLOSE_DELAY = 260;  /* grace after the pointer leaves trigger + panel */

  var openItem = null;
  var openTimer = 0;
  var closeTimer = 0;
  var lastPointer = 'mouse';
  var uid = 0;

  document.addEventListener('pointerdown', function (e) {
    lastPointer = e.pointerType || 'mouse';
    if (openItem && !openItem.contains(e.target)) closeItem(openItem);
  }, true);

  function buttonOf(item) {
    return item.querySelector(':scope > .\\#main-nav-disclosure');
  }

  function openItemNow(item) {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    if (openItem && openItem !== item) closeItem(openItem);
    openItem = item;
    item.classList.add('!active');
    var btn = buttonOf(item);
    if (btn) btn.setAttribute('aria-expanded', 'true');
  }

  function closeItem(item) {
    item.classList.remove('!active');
    var btn = buttonOf(item);
    if (btn) btn.setAttribute('aria-expanded', 'false');
    if (openItem === item) openItem = null;
  }

  function setupItem(item) {
    if (item.dataset.navIntent) return;
    var link = item.querySelector(':scope > .\\#main-nav-link');
    var drop = item.querySelector(':scope > .\\#main-nav-dropdown');
    /* click-to-open mode belongs to the stock main-nav.js */
    if (!link || !drop || item.classList.contains('@click-open')) return;
    item.dataset.navIntent = '1';

    if (!drop.id) drop.id = 'main-nav-dropdown-' + (++uid);
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = '#main-nav-disclosure';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', drop.id);
    btn.setAttribute('aria-label', link.textContent.replace(/\s+/g, ' ').trim());
    /* the caret is the trailing icon (title text precedes it); a leading
       block icon with carets switched off stays in the link */
    var icons = link.querySelectorAll(':scope > .\\#icon');
    var caret = icons.length ? icons[icons.length - 1] : null;
    if (caret) {
      var before = '';
      for (var n = link.firstChild; n && n !== caret; n = n.nextSibling) before += n.textContent;
      if (before.trim()) btn.appendChild(caret);
    }
    link.after(btn);

    item.classList.add('@click-open', '@intent');
    if (item.classList.contains('lights-off-hover')) {
      item.classList.remove('lights-off-hover');
      item.classList.add('lights-off-active');
    }

    item.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'touch' || !DESKTOP.matches) return;
      clearTimeout(closeTimer);
      if (openItem === item) return;
      clearTimeout(openTimer);
      openTimer = setTimeout(function () { openItemNow(item); }, openItem ? SWITCH_DELAY : OPEN_DELAY);
    });

    item.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'touch') return;
      clearTimeout(openTimer);
      if (openItem !== item) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        /* stay open only while the keyboard is working inside the panel */
        if (openItem === item && !item.querySelector(':focus-visible')) closeItem(item);
      }, CLOSE_DELAY);
    });

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      /* keyboard (detail 0) and touch toggle; a mouse click only ever opens */
      if (openItem === item && (e.detail === 0 || lastPointer === 'touch')) closeItem(item);
      else openItemNow(item);
    });

    link.addEventListener('click', function (e) {
      /* touch on a wide screen: first tap opens the panel, second follows */
      if (lastPointer === 'touch' && DESKTOP.matches && openItem !== item) {
        e.preventDefault();
        openItemNow(item);
      }
    });

    item.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openItem === item) {
        e.stopPropagation();
        closeItem(item);
        btn.focus();
      }
    });

    item.addEventListener('focusout', function (e) {
      if (openItem !== item) return;
      var next = e.relatedTarget;
      if (next && !item.contains(next)) closeItem(item);
    });
  }

  DESKTOP.addEventListener('change', function () {
    if (openItem) closeItem(openItem);
  });

  /* ---- Mobile drawer ---- */
  function makeButton(el, name) {
    if (el.dataset.navButton) return;
    el.dataset.navButton = '1';
    el.setAttribute('role', 'button');
    if (el.tabIndex < 0 || !el.hasAttribute('tabindex')) el.tabIndex = 0;
    if (name) el.setAttribute('aria-label', name);
    /* modal-open already handles Enter itself */
    var ownEnter = el.tagName === 'MODAL-OPEN';
    el.addEventListener('keydown', function (e) {
      if (e.target !== el) return;
      if (e.key === ' ' || e.key === 'Spacebar' || (e.key === 'Enter' && !ownEnter)) {
        e.preventDefault();
        el.click();
      }
    });
  }

  function focusTarget(el) {
    if (!el) return;
    if (!el.matches('a[href], button, [tabindex]')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  }

  function setupDrawer(nav) {
    if (nav.dataset.navA11y) return;
    nav.dataset.navA11y = '1';
    var levels = Array.prototype.slice.call(nav.querySelectorAll('.\\#main-nav-mobile-level'));
    var openers = [];

    function syncInert(level) {
      levels.forEach(function (l) {
        if (+l.dataset.level === level) l.removeAttribute('inert');
        else l.setAttribute('inert', '');
      });
    }

    nav.querySelectorAll('label').forEach(function (label) {
      var radio = label.querySelector('input.\\#main-nav-mobile-level-trigger');
      if (label.htmlFor) makeButton(label);                   /* forward row */
      else if (radio) makeButton(label, LABEL.back);          /* back control */
    });

    var close = nav.querySelector('.\\#main-nav-mobile-close');
    if (close) makeButton(close, LABEL.close);

    nav.addEventListener('change', function (e) {
      var radio = e.target;
      if (!radio.matches || !radio.matches('.\\#main-nav-mobile-level-trigger')) return;
      var level = +radio.value;
      syncInert(level);
      if (radio.id) {
        /* forward: remember the row, focus the new panel's heading */
        openers[level] = nav.querySelector('label[for="' + radio.id + '"]');
        var content = radio.nextElementSibling;
        var heading = content && content.querySelector('.\\#main-nav-mobile-heading');
        focusTarget(heading || (content && content.querySelector('a[href], label')));
      } else {
        /* back: return focus to the row that opened the panel */
        focusTarget(openers[level + 1]);
      }
    });

    syncInert(0);

    var dialog = nav.closest('dialog');
    if (!dialog) return;

    var rootHeading = nav.querySelector('.\\#main-nav-mobile-level[data-level="0"] .\\#main-nav-mobile-heading');
    if (rootHeading) {
      if (!rootHeading.id) rootHeading.id = 'main-nav-mobile-heading-' + (++uid);
      rootHeading.setAttribute('tabindex', '-1');
      rootHeading.setAttribute('autofocus', '');
      dialog.setAttribute('aria-labelledby', rootHeading.id);
    } else {
      dialog.setAttribute('aria-label', LABEL.menu);
    }

    /* native Escape skips modal-container._close(); route it through it */
    dialog.addEventListener('cancel', function (e) {
      e.preventDefault();
      dialog.dispatchEvent(new CustomEvent('modal-close', { bubbles: true }));
    });

    dialog.addEventListener('close', function () {
      nav.querySelectorAll('input.\\#main-nav-mobile-level-trigger').forEach(function (r) { r.checked = false; });
      nav.style.setProperty('--main-nav-mobile-level', 0);
      levels.forEach(function (l) { l.scrollTop = 0; });
      openers = [];
      syncInert(0);
    });
  }

  /* ---- Header icon triggers (search, cart, mobile menu) ---- */
  function setupTrigger(el) {
    if (el.dataset.navButton) return;
    var target = document.getElementById(el.getAttribute('target') || '');
    var name = null;
    if (!el.textContent.trim() || el.classList.contains('#header-mobile-link')) {
      if ((el.getAttribute('target') || '') === 'cart-drawer') name = LABEL.cart;
      else if (target && target.querySelector('main-nav-mobile')) name = LABEL.menu;
      else if (target && target.querySelector('input[type="search"], input[name="q"]')) name = LABEL.search;
    }
    makeButton(el, name);
  }

  function init() {
    document.querySelectorAll('.\\#main-nav .\\#main-nav-item').forEach(function (item) {
      if (!item.parentElement.closest('.\\#main-nav-dropdown')) setupItem(item);
    });
    document.querySelectorAll('main-nav-mobile').forEach(setupDrawer);
    document.querySelectorAll('.\\#header modal-open').forEach(setupTrigger);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

(function () {
  'use strict';
  var WIDE = window.matchMedia('(min-width: 992px)');
  var SEL = '.\\#main-nav-dropdown .\\#main-nav-grid.\\@spaced.\\@layout\\:columns';

  function fit() {
    var grids = document.querySelectorAll(SEL);
    for (var i = 0; i < grids.length; i++) grids[i].classList.remove('@pro-wrap');
    if (!WIDE.matches) return;
    for (var k = 0; k < grids.length; k++) {
      if (grids[k].scrollWidth > grids[k].clientWidth + 1) grids[k].classList.add('@pro-wrap');
    }
  }

  var timer = 0;
  function later() {
    clearTimeout(timer);
    timer = setTimeout(fit, 120);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fit);
  else fit();
  window.addEventListener('resize', later);
  window.addEventListener('load', later);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(later);
})();

(function () {
  'use strict';

  function hydrate(root, attr) {
    if (!root || !root.querySelectorAll) return;
    var imgs = root.querySelectorAll('img[' + attr + ']');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      /* the closed dropdown may not intersect the viewport, where
         loading="lazy" would defer the fetch forever — force it */
      img.loading = 'eager';
      var srcset = img.getAttribute(attr + 'set');
      if (srcset) {
        img.setAttribute('srcset', srcset);
        img.removeAttribute(attr + 'set');
      }
      img.setAttribute('src', img.getAttribute(attr));
      img.removeAttribute(attr);
    }
  }

  /* --- 1. Hydrate on intent (mouse, touch, keyboard) --- */
  function onIntent(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var navItem = t.closest('.\\#main-nav-item, .\\#main-nav-mobile');
    if (navItem) hydrate(navItem, 'data-nav-src');
    var loc = t.closest('.\\#localization-dropdown, .\\#footer-localization-item');
    if (loc) hydrate(loc, 'data-flag-src');
  }
  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);
  document.addEventListener('click', onIntent, { passive: true });

  /* --- 2. Background warm-up (menu images only, never flags) --- */
  var conn = navigator.connection;
  if (conn && (conn.saveData || /(^|[^3-9])2g/.test(conn.effectiveType || ''))) return;

  function warmUp() {
    var scopes = document.querySelectorAll('.\\#main-nav-dropdown, .\\#main-nav-mobile');
    var i = 0;
    (function next() {
      if (i >= scopes.length) return;
      var imgs = scopes[i].querySelectorAll('img[data-nav-src]');
      for (var j = 0; j < imgs.length; j++) {
        if ('fetchPriority' in imgs[j]) imgs[j].fetchPriority = 'low';
      }
      hydrate(scopes[i], 'data-nav-src');
      i++;
      setTimeout(next, 350);
    })();
  }

  function schedule() {
    var idle = window.requestIdleCallback || function (fn) { setTimeout(fn, 2500); };
    idle(warmUp, { timeout: 10000 });
  }
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule);
})();
